import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRowHighlight } from '../utils/useRowHighlight';
import { useAppShell } from '../contexts/AppShellContext';
import { useChats } from '../contexts/ChatsContext';
import { DEMO_EMAILS, DemoEmail } from '../data/demoEmails';

const EMAIL_META: Record<string, {
  jobId: string;
  priority: 'urgent' | 'high';
  sla: string;
}> = {
  'email-001': { jobId: 'CA-2405-001', priority: 'high',   sla: '8h left' },
  'email-002': { jobId: 'CA-2405-002', priority: 'urgent', sla: '2h left' },
};

const PRIORITY_CLS = {
  urgent: 'bg-red-100 text-red-700',
  high:   'bg-orange-100 text-orange-700',
};

const STATUS_CLS: Record<string, string> = {
  'New':        'bg-blue-100 text-blue-700',
  'Processing': 'bg-orange-100 text-orange-700',
  'Responded':  'bg-green-100 text-green-700',
  'Follow-up':  'bg-purple-100 text-purple-700',
};

const COL = '110px 170px 130px 1fr 110px 90px 90px';

export function EmailIntakePage() {
  const { setTopbar } = useAppShell();
  const { sessions } = useChats();
  const navigate = useNavigate();
  const { rowRef, highlightClass } = useRowHighlight('emailId');

  useEffect(() => {
    setTopbar('Email Intake Queue', '');
  }, [setTopbar]);

  function getEmailStatus(emailId: string): 'New' | 'Processing' | 'Responded' | 'Follow-up' {
    const sess = sessions.find(s => s.kind === 'email' && s.caEmail?.id === emailId);
    if (!sess) return 'New';
    if (sess.caApproved) return 'Responded';
    return 'Processing';
  }

  const statuses = DEMO_EMAILS.map(e => getEmailStatus(e.id));
  const newCount    = statuses.filter(s => s === 'New').length;
  const urgentCount = Object.values(EMAIL_META).filter(m => m.priority === 'urgent').length;

  function handleEmailClick(email: DemoEmail) {
    navigate('/ace', { state: { caEmail: email } });
  }

  return (
    <div className="h-full flex flex-col overflow-hidden bg-wash">
      {/* Page header */}
      <div className="px-8 pt-6 pb-5 flex-shrink-0 bg-white border-b border-trim">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-base font-semibold text-ink">Email Intake Queue</h1>
            <p className="text-xs text-muted mt-0.5">Incoming advisor emails requiring CA processing</p>
          </div>
          <div className="flex items-center gap-6">
            {[
              { label: 'Total',  val: DEMO_EMAILS.length, cls: 'text-ink' },
              { label: 'New',    val: newCount,            cls: 'text-orange-600' },
              { label: 'Urgent', val: urgentCount,         cls: 'text-red-600' },
            ].map(s => (
              <div key={s.label} className="text-center">
                <div className={`text-2xl font-bold leading-none ${s.cls}`}>{s.val}</div>
                <div className="text-xs text-muted mt-1">{s.label}</div>
              </div>
            ))}
            <button className="ml-2 px-3 py-1.5 text-xs font-medium text-copy border border-trim rounded-lg hover:bg-wash transition-colors">
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Queue table */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {DEMO_EMAILS.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted">
            <p className="text-sm">No items in queue</p>
          </div>
        ) : (
          <div className="bg-white border border-trim rounded-xl overflow-hidden shadow-card">
            {/* Header */}
            <div
              className="grid gap-4 px-5 py-3 border-b border-trim bg-gray-50 text-xs font-semibold text-muted uppercase tracking-widest"
              style={{ gridTemplateColumns: COL }}
            >
              <div>Job ID</div>
              <div>Advisor</div>
              <div>Company</div>
              <div>Subject</div>
              <div>Received</div>
              <div>Priority</div>
              <div>Status</div>
            </div>

            {/* Rows */}
            {DEMO_EMAILS.map((email, i) => {
              const meta = EMAIL_META[email.id];
              const status = getEmailStatus(email.id);
              return (
                <button
                  key={email.id}
                  ref={rowRef(email.id)}
                  onClick={() => handleEmailClick(email)}
                  className={`w-full text-left grid gap-4 px-5 py-4 hover:bg-tint transition-colors items-center overflow-hidden ${highlightClass(email.id)} ${
                    i < DEMO_EMAILS.length - 1 ? 'border-b border-trim' : ''
                  }`}
                  style={{ gridTemplateColumns: COL }}
                >
                  {/* Job ID */}
                  <div className="font-mono text-xs font-semibold text-ink">{meta?.jobId ?? '—'}</div>

                  {/* Advisor */}
                  <div className="min-w-0 overflow-hidden">
                    <div className="text-sm font-medium text-ink truncate">{email.from}</div>
                    <div className="text-xs text-muted truncate">{email.fromEmail}</div>
                  </div>

                  {/* Company */}
                  <div className="flex flex-col gap-1">
                    <div className="text-sm font-medium text-ink">{email.companyName}</div>
                    <span className="inline-block text-xs font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 w-fit">{email.ticker}</span>
                  </div>

                  {/* Subject */}
                  <div className="min-w-0 overflow-hidden">
                    <div className="text-sm font-medium text-ink truncate">{email.subject}</div>
                    <div className="text-xs text-muted truncate mt-0.5">{email.body.split('\n')[0]}</div>
                  </div>

                  {/* Received */}
                  <div className="text-xs text-muted">{email.date}</div>

                  {/* Priority + SLA */}
                  <div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${PRIORITY_CLS[meta?.priority ?? 'high']}`}>
                      {meta?.priority === 'urgent' ? 'Urgent' : 'High'}
                    </span>
                    {meta && (
                      <div className={`text-xs mt-1 font-medium ${meta.priority === 'urgent' ? 'text-red-600' : 'text-orange-600'}`}>
                        {meta.sla}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${STATUS_CLS[status]}`}>
                      {status}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
